
export type SubscriptionStatus = 'free' | 'trialing' | 'active' | 'expired' | 'canceled';

export interface User {
  id: string;
  email: string;
  name: string;
  subscription_status: SubscriptionStatus;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  current_period_end?: string;
  trial_end: string;
  created_at: string;
  daily_query_count?: number;
  last_query_date?: string;
}

export interface RemedyDocument {
  id: string;
  condition: string;
  content: string;
  source: 'Ayurveda' | 'Naturopathy' | 'Yoga' | 'Diet' | string;
  book_name?: string;
  similarity?: number;
}

export type SearchSource = 'RAG' | 'AI' | 'Search';

export interface RecommendationMetadata {
  type: 'YOGA' | 'DIET' | 'REMEDY' | 'WISDOM';
  id: string;
  title: string;
  summary: string;
  detail?: string;
  sourceBook?: string;
}

export interface Message {
  id: string;
  role: 'user' | 'model';
  content: string;
  timestamp: number;
  sources?: RemedyDocument[];
  recommendations?: RecommendationMetadata[];
  suggestions?: string[];
}

export type SingleDosha = 'vata' | 'pitta' | 'kapha';

export type DualDosha =
  | 'vata-pitta'
  | 'pitta-kapha'
  | 'vata-kapha';

export type DoshaType = SingleDosha | DualDosha | 'sama';

export function isSama(dosha: DoshaType | null | string): boolean {
  return dosha === 'sama';
}

export function isDual(dosha: DoshaType | null | string): boolean {
  return typeof dosha === 'string' && dosha.includes('-') && dosha !== 'sama';
}

export function getDoshas(dosha: DoshaType | null | string): SingleDosha[] {
  if (!dosha) return [];
  if (dosha === 'sama') return ['vata', 'pitta', 'kapha'];
  if (isDual(dosha)) return (dosha as string).split('-') as SingleDosha[];
  return [dosha as SingleDosha];
}

export interface UserProfile {
  id: string;
  user_intent?: 'wellness' | 'condition';
  primary_concern?: string;
  prakriti_dosha?: DoshaType;
  vikriti_dosha?: DoshaType;
  vikriti_last_updated?: string;
  age?: number;
  sex?: string;
  height?: number; // cm
  weight?: number; // kg
  primary_goal?: string;
  occupation?: string;
  lifestyle?: string; // Keeping for backward compatibility or general "Lifestyle" text
  dietary_preference?: string;
  activity_level?: number; // 1-10
  sleep_pattern?: string;
  stress_level?: number; // 1-10
  allergies?: string[];
  medications?: string[];
  lab_metrics?: Record<string, string>;
  life_stage?: string;
  menstrual_pattern?: string[];
  hormonal_context?: string[];
  flow_color?: string;
  health_data?: {
    medical_history?: string | string[]; // Changed to allow array for multi-select
    [key: string]: any;
  };
  updated_at?: string;
  daily_query_count?: number;
  last_query_date?: string;
}

export enum AppView {
  AUTH = 'AUTH',
  CHAT = 'CHAT',
  PROFILE = 'PROFILE',
  ANALYTICS = 'ANALYTICS',
  ACCOUNT = 'ACCOUNT',
  YOGA = 'YOGA',
  DIET = 'DIET',
  BOTANICAL = 'BOTANICAL',
  LIBRARY = 'LIBRARY',
  BRANDING = 'BRANDING',
  LEGAL = 'LEGAL',
  ABOUT = 'ABOUT',
  VOICE = 'VOICE',
  FAQ = 'FAQ',
  WISDOM = 'WISDOM'
}

export interface QueryUsage {
  count: number;
  limit: number;
  remaining: number;
  isUnlimited: boolean;
}

export interface FeatureContext {
  id: string;
  title: string;
  detail?: string;
  sourceBook?: string;
  cachedPoses?: YogaPose[];
  cachedPlan?: DayPlan[];
  cachedNutriHeal?: any; // For new redesigned plan
  isFromLibrary?: boolean;
}

export interface ImageResult {
  url: string | null;
  source: string;
  confidence: number;
}

export interface YogaPose {
  pose_name: string;
  type: 'Asana' | 'Pranayama' | 'Exercise';
  benefit: string;
  instructions: string;
  contraindications: string;
  image_url?: string;
  image?: ImageResult;
  phase?: string;
  vector_description?: string; // High-quality prompt context
  _imagePath?: string; // Internal hint for local assets
  doshaAdaptation?: {
    vata: { duration: string; breathPattern: string; intensity: string; props: string; mentalFocus: string; };
    pitta: { duration: string; breathPattern: string; intensity: string; props: string; mentalFocus: string; };
    kapha: { duration: string; breathPattern: string; intensity: string; props: string; mentalFocus: string; };
  };
  doshaNote?: string;
}

export interface YogaRoutine {
  poses: YogaPose[];
  classification: 'Acute' | 'Chronic';
  duration: string;
  phaseEmphasis?: {
    dominantDosha: string;
    foundationWeight: 'extended' | 'standard' | 'reduced';
    activationWeight: 'extended' | 'standard' | 'reduced';
    internalBalanceWeight: 'extended' | 'standard' | 'reduced';
    pacingDescriptor: string;
    sequencingRationale: string;
  };
  natureIntegrationDetail?: NatureIntegrationDetail;
}

export interface NatureIntegrationDetail {
  activityName: string;
  benefit: string;
  textualSource: string;
  doshaGuidance: {
    vata: { environment: string; timing: string; avoidance: string; };
    pitta: { environment: string; timing: string; avoidance: string; };
    kapha: { environment: string; timing: string; avoidance: string; };
  };
  steps: {
    stepNumber: number;
    title: string;
    description: string;
    duration: string;
  }[];
  sensoryPractice: {
    sight: string;
    sound: string;
    smell: string;
    touch: string;
  };
  optimalTiming: {
    vata: string;
    pitta: string;
    kapha: string;
  };
  contraindications: string;
  imageSearchQuery: string;
}

export interface Meal {
  day: string;
  type: string;
  dish_name: string;
  search_query: string;
  ingredients: string[];
  benefit: string;
  medicinal_purpose?: string;
  preparation_instructions?: string | string[];
  image_url?: string;
  image?: ImageResult;
}

export interface DayPlan {
  day: string;
  meals: Meal[];
}

export interface NutriHealPlan {
  ailment: string;
  category: string;
  personalizationNote: string;
  guidelines: {
    embrace: string[];
    avoid: string[];
    switches: string[];
    timing: string;
  };
  primaryRecipes: any[]; // Using any[] to avoid circular dependency or complex imports here
  herbalTonics: any[];
  duration: string;
}

export interface SavedMealPlan {
  id: string;
  user_id: string;
  title: string;
  created_at: string;
  plan_data: NutriHealPlan | DayPlan[] | any;
}

export interface SavedYogaPlan {
  id: string;
  user_id: string;
  title: string;
  created_at: string;
  poses: YogaPose[];
}

export interface WisdomShlok {
  id: string | number;
  source_text: string;
  chapter_verse: string;
  content: string;
  sanskrit_devanagari?: string;
  transliteration?: string;
  english_translation?: string;
  kosha_layer: 'manomaya' | 'vijnanamaya' | 'anandamaya';
  life_guidance: string | null;
  tags: string[];
}
